import type { Element, ElementContent, Parent, Text } from 'hast';
import {
  buildRecordRoute,
  isEntityIdQuery,
  RECORD_ID_REGEX,
} from '../../utils/recordIds';

export const getRecordRouteForId = (id: string): string | null =>
  isEntityIdQuery(id) ? buildRecordRoute(id) : null;

const isText = (node: ElementContent): node is Text => node.type === 'text';

const containsRecordId = (value: string): boolean => {
  RECORD_ID_REGEX.lastIndex = 0;
  return RECORD_ID_REGEX.test(value);
};

const SKIP_PARENT_TAGS = new Set(['a', 'code', 'pre']);

const splitTextNodeIntoLinks = (value: string): ElementContent[] => {
  const segments: ElementContent[] = [];
  let lastIndex = 0;

  RECORD_ID_REGEX.lastIndex = 0;
  let match = RECORD_ID_REGEX.exec(value);
  while (match !== null) {
    const [id] = match;
    const route = getRecordRouteForId(id);

    if (route) {
      if (match.index > lastIndex) {
        segments.push({
          type: 'text',
          value: value.slice(lastIndex, match.index),
        });
      }
      segments.push({
        type: 'element',
        tagName: 'a',
        properties: { href: route },
        children: [{ type: 'text', value: id }],
      });
      lastIndex = match.index + id.length;
    }

    match = RECORD_ID_REGEX.exec(value);
  }

  if (lastIndex < value.length) {
    segments.push({ type: 'text', value: value.slice(lastIndex) });
  }

  return segments;
};

export const rehypeLinkifyRecordIds = () => (tree: Parent) => {
  const visit = (node: Parent, skip: boolean) => {
    const children = node.children as ElementContent[] | undefined;
    if (!Array.isArray(children)) {
      return;
    }

    const nextChildren: ElementContent[] = [];

    for (const child of children) {
      if (!skip && isText(child) && containsRecordId(child.value)) {
        nextChildren.push(...splitTextNodeIntoLinks(child.value));
        continue;
      }

      if (child.type === 'element') {
        const childElement = child as Element;
        visit(childElement, skip || SKIP_PARENT_TAGS.has(childElement.tagName));
      }
      nextChildren.push(child);
    }

    node.children = nextChildren;
  };

  visit(tree, false);
};
